import React, { useState, useEffect } from 'react';
import WorkspaceSidebar from '../../components/ui/WorkspaceSidebar';
import UserProfileDropdown from '../../components/ui/UserProfileDropdown';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ApprovalCard from './components/ApprovalCard';
import DocumentPreview from './components/DocumentPreview';
import SignatureCapture from './components/SignatureCapture';
import AuditTrail from './components/AuditTrail';
import DelegationModal from './components/DelegationModal';

const DigitalSignoffs = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('pending'); // pending, completed, delegated
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showSignature, setShowSignature] = useState(false);
  const [showAuditTrail, setShowAuditTrail] = useState(false);
  const [showDelegation, setShowDelegation] = useState(false);
  const [documents, setDocuments] = useState([]);

  // Mock documents data
  const mockDocuments = [
    {
      id: 'doc-001',
      title: 'Order-to-Cash BPD² Implementation Guide',
      description: 'Comprehensive business process design document for O2C implementation with SAP Best Practices',
      type: 'BPD',
      version: '2.1',
      status: 'pending',
      createdAt: '2024-10-08T10:00:00Z',
      deadline: '2024-10-15T17:00:00Z',
      canSign: true,
      canDelegate: true,
      signers: [
        {
          name: 'Michael Rodriguez',
          role: 'Solution Architect',
          status: 'pending',
          email: 'michael.rodriguez@enterprise.com'
        },
        {
          name: 'Sarah Chen',
          role: 'Business Analyst',
          status: 'signed',
          signedAt: '2024-10-09T14:30:00Z',
          email: 'sarah.chen@enterprise.com'
        },
        {
          name: 'David Kim',
          role: 'Technical Lead',
          status: 'pending',
          email: 'david.kim@enterprise.com'
        }
      ],
      comments: [
        {
          author: 'Sarah Chen',
          text: 'The integration requirements section needs clarification on the API endpoints.',
          page: 3,
          timestamp: '2024-10-09T12:20:00Z'
        },
        {
          author: 'David Kim',
          text: 'Please review the security considerations for external system connections.',
          page: 7,
          timestamp: '2024-10-09T15:45:00Z'
        }
      ]
    },
    {
      id: 'doc-002',
      title: 'Procure-to-Pay FSD² Technical Specification',
      description: 'Functional specification document detailing P2P process configuration and customizations',
      type: 'FSD',
      version: '1.3',
      status: 'in-progress',
      createdAt: '2024-10-07T09:15:00Z',
      deadline: '2024-10-12T17:00:00Z',
      canSign: false,
      canDelegate: true,
      signers: [
        {
          name: 'Jennifer Walsh',
          role: 'Project Manager',
          status: 'signed',
          signedAt: '2024-10-08T11:20:00Z',
          email: 'jennifer.walsh@enterprise.com'
        },
        {
          name: 'Robert Johnson',
          role: 'Enterprise Architect',
          status: 'pending',
          email: 'robert.johnson@enterprise.com'
        }
      ],
      comments: []
    },
    {
      id: 'doc-003',
      title: 'Record-to-Report BPD² Process Framework',
      description: 'Business process design for financial closing and reporting procedures',
      type: 'BPD',
      version: '1.0',
      status: 'overdue',
      createdAt: '2024-10-05T14:30:00Z',
      deadline: '2024-10-09T17:00:00Z',
      canSign: true,
      canDelegate: true,
      signers: [
        {
          name: 'Michael Rodriguez',
          role: 'Solution Architect',
          status: 'pending',
          email: 'michael.rodriguez@enterprise.com'
        },
        {
          name: 'Lisa Anderson',
          role: 'Finance Lead',
          status: 'pending',
          email: 'lisa.anderson@enterprise.com'
        }
      ],
      comments: [
        {
          author: 'Lisa Anderson',
          text: 'The month-end closing timeline needs to be adjusted based on our current processes.',
          page: 5,
          timestamp: '2024-10-08T16:30:00Z'
        }
      ]
    },
    {
      id: 'doc-004',
      title: 'Warehouse Management FSD² Configuration Guide',
      description: 'Technical specification for EWM implementation and integration points',
      type: 'FSD',
      version: '2.0',
      status: 'completed',
      createdAt: '2024-10-01T08:00:00Z',
      deadline: '2024-10-08T17:00:00Z',
      canSign: false,
      canDelegate: false,
      signers: [
        {
          name: 'Michael Rodriguez',
          role: 'Solution Architect',
          status: 'signed',
          signedAt: '2024-10-07T13:45:00Z',
          email: 'michael.rodriguez@enterprise.com'
        },
        {
          name: 'Tom Wilson',
          role: 'Warehouse Manager',
          status: 'signed',
          signedAt: '2024-10-07T16:20:00Z',
          email: 'tom.wilson@enterprise.com'
        },
        {
          name: 'Sarah Chen',
          role: 'Business Analyst',
          status: 'signed',
          signedAt: '2024-10-08T09:15:00Z',
          email: 'sarah.chen@enterprise.com'
        }
      ],
      comments: []
    }
  ];

  useEffect(() => {
    setDocuments(mockDocuments);
  }, []);

  const getFilteredDocuments = () => {
    switch (activeTab) {
      case 'pending':
        return documents?.filter(doc => doc?.status === 'pending' || doc?.status === 'in-progress' || doc?.status === 'overdue');
      case 'completed':
        return documents?.filter(doc => doc?.status === 'completed');
      case 'delegated':
        return documents?.filter(doc => doc?.signers?.some(signer => signer?.status === 'delegated'));
      default:
        return documents;
    }
  };

  const handleReviewDocument = (document) => {
    setSelectedDocument(document);
    setShowPreview(true);
  };

  const handleSignDocument = (document) => {
    setSelectedDocument(document);
    setShowSignature(true);
  };

  const handleDelegateApproval = (document) => {
    setSelectedDocument(document);
    setShowDelegation(true);
  };

  const handleViewAuditTrail = (document) => {
    setSelectedDocument(document);
    setShowAuditTrail(true);
  };

  const handleSignatureComplete = (documentId, signatureData) => {
    setDocuments(prev => prev?.map(doc => {
      if (doc?.id === documentId) {
        const updatedSigners = doc?.signers?.map(signer => {
          if (signer?.email === 'michael.rodriguez@enterprise.com') {
            return {
              ...signer,
              status: 'signed',
              signedAt: new Date()?.toISOString()
            };
          }
          return signer;
        });
        
        const allSigned = updatedSigners?.every(signer => signer?.status === 'signed');
        
        return {
          ...doc,
          signers: updatedSigners,
          status: allSigned ? 'completed' : doc?.status,
          canSign: false
        };
      }
      return doc;
    }));
    
    setShowSignature(false);
    setSelectedDocument(null);
  };

  const handleDelegationComplete = (documentId, delegationData) => {
    setDocuments(prev => prev?.map(doc => {
      if (doc?.id === documentId) {
        return {
          ...doc,
          canSign: false,
          canDelegate: false,
          signers: doc?.signers?.map(signer => {
            if (signer?.email === 'michael.rodriguez@enterprise.com') {
              return {
                ...signer,
                status: 'delegated',
                delegatedTo: delegationData?.delegatedTo,
                delegationReason: delegationData?.reason
              };
            }
            return signer;
          })
        };
      }
      return doc;
    }));
    
    setShowDelegation(false);
    setSelectedDocument(null);
  };

  const handleAddComment = (documentId, comment, page) => {
    setDocuments(prev => prev?.map(doc => {
      if (doc?.id === documentId) {
        return {
          ...doc,
          comments: [
            ...doc?.comments,
            {
              author: 'Michael Rodriguez',
              text: comment,
              page: page,
              timestamp: new Date()?.toISOString()
            }
          ]
        };
      }
      return doc;
    }));
  };

  const handleExportAuditReport = (documentId) => {
    console.log('Exporting audit report for document:', documentId);
    // Mock export functionality
  };

  const getTabCounts = () => {
    const pending = documents?.filter(doc => 
      doc?.status === 'pending' || doc?.status === 'in-progress' || doc?.status === 'overdue'
    )?.length;
    const completed = documents?.filter(doc => doc?.status === 'completed')?.length;
    const delegated = documents?.filter(doc => 
      doc?.signers?.some(signer => signer?.status === 'delegated')
    )?.length;
    
    return { pending, completed, delegated };
  };

  const tabCounts = getTabCounts();
  const filteredDocuments = getFilteredDocuments();

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <WorkspaceSidebar 
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      {/* Main Content */}
      <div className={`${sidebarCollapsed ? 'ml-16' : 'ml-64'} nav-transition pb-16 md:pb-0`}>
        {/* Header */}
        <header className="bg-card border-b border-border px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h1 className="text-2xl font-semibold text-foreground font-inter">
                Digital Signoffs
              </h1>
            </div>
            <UserProfileDropdown />
          </div>
        </header>

        {/* Content */}
        <main className="p-6">
          <BreadcrumbNavigation />

          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-xl font-semibold text-foreground mb-2">
                Document Approval Workflows
              </h2>
              <p className="text-muted-foreground">
                Manage multi-signer approval processes for BPD² and FSD² documents with complete audit trails
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                iconName="Download"
                iconPosition="left"
              >
                Export Reports
              </Button>
              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
              >
                New Approval
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-warning/10 rounded-lg flex items-center justify-center">
                  <Icon name="Clock" size={24} className="text-warning" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{tabCounts?.pending}</p>
                  <p className="text-sm text-muted-foreground">Pending Approvals</p>
                </div>
              </div>
            </div>
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
                  <Icon name="CheckCircle2" size={24} className="text-success" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{tabCounts?.completed}</p>
                  <p className="text-sm text-muted-foreground">Completed</p>
                </div>
              </div>
            </div>
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="UserCheck" size={24} className="text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{tabCounts?.delegated}</p>
                  <p className="text-sm text-muted-foreground">Delegated</p>
                </div>
              </div>
            </div>
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                  <Icon name="TrendingUp" size={24} className="text-accent" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">94%</p>
                  <p className="text-sm text-muted-foreground">On-time Rate</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-border mb-6">
            <nav className="flex space-x-8">
              {[
                { key: 'pending', label: 'Pending Approvals', count: tabCounts?.pending },
                { key: 'completed', label: 'Completed', count: tabCounts?.completed },
                { key: 'delegated', label: 'Delegated', count: tabCounts?.delegated }
              ]?.map((tab) => (
                <button
                  key={tab?.key}
                  onClick={() => setActiveTab(tab?.key)}
                  className={`flex items-center space-x-2 py-3 px-1 border-b-2 nav-transition ${
                    activeTab === tab?.key
                      ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <span className="font-medium">{tab?.label}</span>
                  {tab?.count > 0 && (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      activeTab === tab?.key
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground'
                    }`}>
                      {tab?.count}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          {/* Documents Grid */}
          {filteredDocuments?.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {filteredDocuments?.map((document) => (
                <ApprovalCard
                  key={document?.id}
                  document={document}
                  onReview={handleReviewDocument}
                  onSign={handleSignDocument}
                  onDelegate={handleDelegateApproval}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="FileText" size={32} className="text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                No documents found
              </h3>
              <p className="text-muted-foreground mb-6">
                There are no documents in the {activeTab} category at the moment.
              </p>
              <Button
                variant="outline"
                iconName="Plus"
                iconPosition="left"
              >
                Create New Document
              </Button>
            </div>
          )}

          {/* Quick Actions */}
          <div className="mt-12 bg-card border border-border rounded-lg p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              Quick Actions
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Button
                variant="outline"
                iconName="Shield"
                iconPosition="left"
                onClick={() => handleViewAuditTrail(documents?.[0])}
                className="justify-start"
              >
                View Audit Trails
              </Button>
              <Button
                variant="outline"
                iconName="Download"
                iconPosition="left"
                className="justify-start"
              >
                Export Compliance Report
              </Button>
              <Button
                variant="outline"
                iconName="Settings"
                iconPosition="left"
                className="justify-start"
              >
                Approval Settings
              </Button>
            </div>
          </div>
        </main>
      </div>
      {/* Modals */}
      {showPreview && selectedDocument && (
        <DocumentPreview
          document={selectedDocument}
          onClose={() => {
            setShowPreview(false);
            setSelectedDocument(null);
          }}
          onSign={handleSignDocument}
          onAddComment={handleAddComment}
        />
      )}
      {showSignature && selectedDocument && (
        <SignatureCapture
          document={selectedDocument}
          onSign={handleSignatureComplete}
          onCancel={() => {
            setShowSignature(false);
            setSelectedDocument(null);
          }}
        />
      )}
      {showAuditTrail && selectedDocument && (
        <AuditTrail
          document={selectedDocument}
          onClose={() => {
            setShowAuditTrail(false);
            setSelectedDocument(null);
          }}
          onExportReport={handleExportAuditReport}
        />
      )}
      {showDelegation && selectedDocument && (
        <DelegationModal
          document={selectedDocument}
          onDelegate={handleDelegationComplete}
          onCancel={() => {
            setShowDelegation(false);
            setSelectedDocument(null);
          }}
        />
      )}
    </div>
  );
};

export default DigitalSignoffs;